import { useFormik } from 'formik';
import BaseForm from 'components/atoms/base-form';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseInput from 'components/atoms/base-input';
import { useTranslation } from 'react-i18next';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useEffect, useState, type FC } from 'react';
import BaseTextArea from 'components/atoms/base-textarea';
import BaseButton from 'components/atoms/baseButton';
import EvelynIcon from 'components/atoms/icons/opl-evelyn-monochrome.svg?react';
import BaseSelectBox from 'components/atoms/base-select-box';
import useUser from 'utils/useUser';
import { getTrackerTitle } from 'infrastructure/hooks/shared/useTracker/getTrackerTitle';
import MasterModal from 'components/organisms/masterModal';

import {
  bulkCommentGenerateValidationSchema,
  bulkCommentSubmitValidationSchema,
} from './validation-schema';
import s from './styles.module.scss';

import type { FormikHelpers } from 'formik';
import type {
  ValuesBulkCommentGenerate,
  ValuesBulkCommentSubmit,
} from './validation-schema';

interface ActivityModalBulkFormProps {
  open: boolean;
  onSubmit: (
    values: ValuesBulkCommentSubmit,
    formikHelpers: FormikHelpers<ValuesBulkCommentSubmit>,
  ) => void;
  onGenerateComment: (
    values: ValuesBulkCommentGenerate,
    formikHelpers: FormikHelpers<ValuesBulkCommentGenerate>,
  ) => void;
  onCancelForm: () => void;
  defaultValues: ValuesBulkCommentSubmit;
  generatedComment: string | undefined;
  submitError: string | null;
  commentLoading: boolean;
  reviewLoading: boolean;
  createTrackerLoading: boolean;
  trackerTimer: string;
}

const getDefaultValues = () => {
  return { concerns: 'no' };
};

const ActivityTableBulkModalForm: FC<ActivityModalBulkFormProps> = ({
  open,
  onSubmit,
  onGenerateComment,
  onCancelForm,
  defaultValues,
  generatedComment,
  submitError,
  commentLoading,
  reviewLoading,
  createTrackerLoading,
  trackerTimer,
}) => {
  const { t } = useTranslation();
  const { Alert, showAlert } = useAlert();
  const { isMedicalAssistantRole } = useUser();

  const formikGenerateComment = useFormik<ValuesBulkCommentGenerate>({
    initialValues: bulkCommentGenerateValidationSchema().cast(
      getDefaultValues(),
    ),
    enableReinitialize: true,
    onSubmit: onGenerateComment,
    validateOnChange: false,
    validationSchema: bulkCommentGenerateValidationSchema(),
  });

  const {
    resetForm: resetGenerateCommentForm,
    values,
    submitForm: submitGenerateCommentForm,
    setFieldError: setGenerateCommentFieldError,
  } = formikGenerateComment;

  const onCancel = async () => {
    if (values.concerns !== 'no' && values.comment) {
      const confirmRes = await showAlert({
        title: t('confirm.cancelNote'),
        messageTitle: t('confirm.closeClinicalNotePopup'),
        successBtnLabel: t('controls.yes'),
        rejectBtnLabel: t('controls.no'),
      });

      if (confirmRes) {
        resetGenerateCommentForm();
        onCancelForm();
      }
    } else {
      resetGenerateCommentForm();
      onCancelForm();
    }
  };

  const formikBulkComment = useFormik<ValuesBulkCommentSubmit>({
    initialValues: defaultValues,
    enableReinitialize: true,
    onSubmit,
    validateOnChange: false,
    validationSchema: bulkCommentSubmitValidationSchema(),
  });

  const {
    submitForm: submitBulkForm,
    setFieldValue: setBulkCommentFieldValue,
    resetForm: resetBulkForm,
  } = formikBulkComment;

  useEffect(() => {
    if (!open) {
      resetGenerateCommentForm();
      resetBulkForm();
    }
    if (generatedComment) {
      setBulkCommentFieldValue('comment', generatedComment);
    }
    if (submitError) {
      setGenerateCommentFieldError('readingAvg', submitError);
    }
  }, [open, generatedComment, submitError]);

  const [isMinimized, setIsMinimized] = useState(false);

  const title = isMinimized
    ? t('labels.bulkReadingsCommentMinimized')
    : t('labels.bulkReadingsComment');

  const rightTitle = getTrackerTitle({
    isMinimized,
    trackerTimer,
  });

  const commentSameAsGenerated =
    !!formikBulkComment.values.comment &&
    formikBulkComment.values.comment === generatedComment;

  return (
    <>
      {open && (
        <MasterModal
          title={title}
          isOpen={open}
          toggle={onCancel}
          rightTitle={rightTitle}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
          onSave={submitBulkForm}
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
          isSaveLoading={reviewLoading}
          isSaveDisabled={
            !formikBulkComment.dirty ||
            createTrackerLoading ||
            commentSameAsGenerated
          }
          saveLabel={
            commentSameAsGenerated
              ? t('labels.cannotSubmitMustUpdateComment')
              : undefined
          }
        >
          <BaseForm
            formik={formikGenerateComment}
            plaintext={false}
            readonly={false}
            onSave={submitGenerateCommentForm}
            controlsClassName={s['generate-comment-submit']}
          >
            {isMedicalAssistantRole && (
              <>
                <BaseFormItem
                  name="concerns"
                  label="Any concern about data quality issues?"
                  required
                >
                  <BaseSelectBox
                    options={[
                      { label: t('labels.no'), value: 'no' },
                      { label: t('labels.yes'), value: 'yes' },
                    ]}
                    defaultValue={{ label: t('labels.no'), value: 'no' }}
                  />
                </BaseFormItem>
                {formikGenerateComment.values.concerns === 'yes' && (
                  <BaseFormItem
                    name="comment"
                    label={t('labels.comment')}
                    required
                  >
                    <BaseInput />
                  </BaseFormItem>
                )}
              </>
            )}
            {formikGenerateComment.errors.comment &&
              typeof formikGenerateComment.errors.comment === 'string' && (
                <p data-cy="comment-text" className={s['error-text']}>
                  {formikGenerateComment.errors.comment}
                </p>
              )}
            <BaseButton
              label={t('labels.generateAiComment')}
              type="secondary"
              onClick={submitGenerateCommentForm}
              disabled={formikGenerateComment.dirty || commentLoading}
              icon={<EvelynIcon />}
              dataCy="form-save-button"
              className={s['generate-comment-submit']}
            />
          </BaseForm>
          <BaseForm
            formik={formikBulkComment}
            plaintext={false}
            readonly={false}
          >
            <BaseFormItem name="comment">
              <BaseTextArea rows={8} />
            </BaseFormItem>
          </BaseForm>
        </MasterModal>
      )}
      {Alert}
    </>
  );
};
export default ActivityTableBulkModalForm;
